import { html, TemplateResult } from 'lit';
import { OutlineElement } from '@phase2/outline-core';
import { customElement, property } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '@phase2/outline-adopted-stylesheets-controller';

import globalStyles from './pc-tabbed-nav.lightDom.css.lit';

/**
 * The Tabbed Nav component
 * @element pc-tabbed-nav
 * @extends OutlineElement
 * @slot - The default slot for this element.
 */
@customElement('pc-tabbed-nav')
export class PCTabbedNav extends OutlineElement {
  adoptedStylesheets: AdoptedStyleSheets;

  @property({ type: Boolean, attribute: 'preview-mode' })
  previewMode: boolean;

  @property({ type: String, attribute: 'variant', reflect: true })
  variant: 'small' | 'default' = 'default';

  connectedCallback() {
    super.connectedCallback();
    this.adoptedStylesheets = new AdoptedStyleSheets(globalStyles);
    this.addController(this.adoptedStylesheets);
    this._initiateTabs();
  }

  nodesWithPanels: HTMLElement[];

  render(): TemplateResult {
    this._buildTabWrapper();

    return html` <div class="tabbed-nav"><slot></slot></div> `;
  }

  /**
   * Initializes tab navigation within a container element.
   *
   */
  _initiateTabs() {
    const nodesWithPanels: HTMLElement[] = [];
    // Find all container elements with a panel attribute
    const PCContainerPanels = document.querySelectorAll(
      '[panel]:not([in-preview])'
    );

    PCContainerPanels.forEach((panel, index) => {
      !this.previewMode &&
        // Configure accessibility attributes for each panel
        this.configurePanelAttributes(panel as HTMLElement, index);

      // Create tab navigation elements and add them to the array
      const panelHasContent = !!(panel as HTMLElement).children.length;
      if (panelHasContent) {
        const liContainer = this.createTabNavigation(
          panel as HTMLElement,
          index
        );
        nodesWithPanels.push(liContainer as HTMLElement);
      }
    });

    this.nodesWithPanels = nodesWithPanels;
  }

  /**
   * Configures accessibility attributes for a panel.
   *
   * @param {HTMLElement} panel - The panel element.
   * @param {number} index - The index of the panel.
   */
  configurePanelAttributes(panel: HTMLElement, index: number) {
    const isHidden = index !== 0;

    panel.setAttribute('aria-hidden', String(isHidden));
    panel.setAttribute('is-hidden', String(isHidden));
    panel.setAttribute('tabindex', isHidden ? '-1' : '0');
  }

  /**
   * Creates tab navigation elements for a panel.
   *
   * @param {HTMLElement} panel - The panel element.
   * @param {number} index - The index of the panel.
   * @returns {HTMLElement} The created li element containing a tab navigation button.
   */
  createTabNavigation(panel: HTMLElement, index: number) {
    const idElement = panel.getAttribute('id');
    const titleElement = panel.getAttribute('panel');

    if (!idElement || !titleElement) {
      return null;
    }

    const liContainer = document.createElement('li');
    const button = document.createElement('button');

    button.innerText = titleElement;
    button.setAttribute('aria-controls', idElement);
    button.setAttribute('role', 'tab');
    button.setAttribute('aria-selected', `${index === 0}`);
    button.classList.toggle('active', index === 0);

    !this.previewMode &&
      button.addEventListener('click', () => {
        this._setActiveTab(button);
      });

    button.addEventListener('keydown', e => {
      if (e.code === 'Enter' || e.code === 'Space') {
        e.preventDefault();
        this._setActiveTab(button);
      }
    });

    liContainer.appendChild(button);
    liContainer.setAttribute('role', 'presentation');

    return liContainer;
  }

  _buildTabWrapper(): void {
    if (this.nodesWithPanels.length > 1) {
      const ulContainer = document.createElement('ul');

      ulContainer?.classList.add('tablist');
      ulContainer?.classList.add(this.variant);
      ulContainer?.setAttribute('role', 'tablist');
      ulContainer.append(...this.nodesWithPanels);

      setTimeout(() => {
        this?.appendChild(ulContainer);
      });
    }
  }

  /**
   * Sets the selected tab and updates the corresponding panels' visibility.
   *
   * @param {HTMLElement} selectedTab - The tab element to be set as active.
   * @returns {void}
   */
  _setActiveTab(selectedTab: HTMLElement): void {
    const activeTabID = selectedTab.getAttribute('aria-controls');

    if (!activeTabID) {
      return;
    }

    const tabs = this.querySelectorAll('[role="tab"]');
    const panels = document.querySelectorAll('[panel]');

    // Update attributes and classes for each tab based on the selected tab.
    tabs?.forEach(tab => {
      const isSelected = tab === selectedTab;

      tab.setAttribute('aria-selected', String(isSelected));
      tab.setAttribute('tabindex', isSelected ? '0' : '-1');
      tab.classList.toggle('active', isSelected);
    });

    // Update attributes and classes for each panel based on the selected tab.
    panels.forEach(panel => {
      const isSelectedPanel = panel.getAttribute('id') === activeTabID;

      panel.classList.toggle('active', isSelectedPanel);
      panel.setAttribute('aria-hidden', String(!isSelectedPanel));
      panel.setAttribute('is-hidden', String(!isSelectedPanel));
      panel.setAttribute('tabindex', isSelectedPanel ? '0' : '-1');
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-tabbed-nav': PCTabbedNav;
  }
}
